import { useState, useEffect, useRef } from "react";
import famousTokens from "../libs/tokens.json";
import { ArrowDownUp, Loader2 } from "lucide-react";
import { quote } from "../libs/quote";
import { swapTokensHandler } from "../libs/swapTokensHandler";
import { ethers, Contract, formatUnits, BrowserProvider } from "ethers";
import { toast } from "react-toastify";
import axios from "axios";
const { ChainId, Token, Fetcher, Route } = require("@uniswap/sdk-core");
const { Pool, Position, TickMath } = require("@uniswap/v3-sdk");

const baseUrl = `${process.env.REACT_APP_API_URL}/transaction`;

const Swap = ({ walletAddress, onRefresh }) => {
  const [tokens] = useState(famousTokens);
  const [sellToken, setSellToken] = useState(famousTokens[0]);
  const [buyToken, setBuyToken] = useState(famousTokens[1]);
  const [sellAmount, setSellAmount] = useState("");
  const [buyAmount, setBuyAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // dropdown visibility state
  const [coinList, setCoinList] = useState(false);
  const [coinReceiveList, setCoinReceiveList] = useState(false);

  // REFS for detecting outside clicks
  const sellDropdownRef = useRef(null);
  const buyDropdownRef = useRef(null);

  // Fetch Uniswap quote
  const getUniswapQuote = async (amount, tokenIn, tokenOut) => {
    const response = await quote(tokenIn, amount, tokenOut);
    return response;
  };

  const handleSellTokenChange = async (e) => {
    const selectedTokenId = e.target.value;
    const selectedToken = tokens.find((t) => t.id === selectedTokenId);
    if (selectedToken) {
      setSellToken(selectedToken);
      // Recalculate quote if user already typed an amount
      if (sellAmount && buyToken) {
        setQuoteLoading(true);
        const q = await getUniswapQuote(sellAmount, selectedToken, buyToken);
        setBuyAmount(Number.parseFloat(q).toFixed(8));
        setQuoteLoading(false);
      }
    }
  };

  const handleBuyTokenChange = async (e) => {
    const selectedTokenId = e.target.value;
    const selectedToken = tokens.find((t) => t.id === selectedTokenId);
    if (selectedToken) {
      setBuyToken(selectedToken);
      // Recalculate quote if user already typed an amount
      if (sellAmount && sellToken) {
        setQuoteLoading(true);
        const q = await getUniswapQuote(sellAmount, sellToken, selectedToken);
        setBuyAmount(Number.parseFloat(q).toFixed(8));
        setQuoteLoading(false);
      }
    }
  };

  const handleSellAmountChange = async (e) => {
    const amount = e.target.value;
    setSellAmount(amount);
    if (amount && sellToken && buyToken) {
      setQuoteLoading(true);
      const q = await getUniswapQuote(amount, sellToken, buyToken);
      setBuyAmount(Number.parseFloat(q).toFixed(8));
      setQuoteLoading(false);
    } else {
      setBuyAmount("");
    }
  };

  const handleBuyAmountChange = (e) => {
    setBuyAmount(e.target.value);
  };

  const swapTokens = async () => {
    if (
      !walletAddress ||
      !sellToken ||
      !buyToken ||
      !sellAmount ||
      parseFloat(sellAmount) <= 0
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      setLoading(true);
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const tokenContract = new Contract(
        sellToken.id.toLowerCase(),
        ["function balanceOf(address) view returns (uint256)"],
        provider
      );
      const rawBalance = await tokenContract.balanceOf(walletAddress);
      const userBalance = parseFloat(
        formatUnits(rawBalance, sellToken.decimals)
      );
      if (userBalance < parseFloat(sellAmount)) {
        toast.error(`Insufficient ${sellToken.symbol} balance.`);
        setLoading(false);
        return;
      }

      const txResult = await swapTokensHandler({
        provider,
        tokenIn: sellToken,
        tokenOut: buyToken,
        amountInDecimal: sellAmount,
        walletAddress,
      });

      if (txResult.success) {
        toast.success("Swap completed successfully!");
        setSellAmount("");
        setBuyAmount("");
        onRefresh();
        await axios.post(baseUrl, {
          tokenIn: sellToken.id,
          tokenOut: buyToken.id,
          amount: parseFloat(sellAmount),
          pair: `${sellToken.symbol}/${buyToken.symbol}`,
          orderPrice: null,
          originalOrderId: null,
          status: "filled",
          transactionHash: txResult.hash,
          filledAt: new Date(),
        });
      } else {
        toast.error(`Swap failed: ${txResult.error}`);
      }
    } catch (error) {
      console.error("Swap error:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleSwapPositions = async () => {
    // swap the two tokens and amounts, then recalc quote
    const tempToken = sellToken;
    const tempAmount = sellAmount;
    setSellToken(buyToken);
    setBuyToken(tempToken);
    setSellAmount(buyAmount);
    setBuyAmount(tempAmount);

    if (buyAmount && buyToken && sellToken) {
      try {
        setQuoteLoading(true);
        const updatedQuote = await getUniswapQuote(
          buyAmount,
          buyToken,
          sellToken
        );
        setBuyAmount(Number.parseFloat(updatedQuote).toFixed(8));
      } catch (error) {
        console.error("Error fetching quote after swap:", error);
      } finally {
        setQuoteLoading(false);
      }
    }
  };

  // Close dropdowns if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If user clicks outside the sell-dropdown wrapper:
      if (
        sellDropdownRef.current &&
        !sellDropdownRef.current.contains(event.target)
      ) {
        setCoinList(false);
      }
      // If user clicks outside the buy-dropdown wrapper:
      if (
        buyDropdownRef.current &&
        !buyDropdownRef.current.contains(event.target)
      ) {
        setCoinReceiveList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sellDropdownRef, buyDropdownRef]);

  // (Optional) 10-second auto-quote update
  useEffect(() => {
    const interval = setInterval(async () => {
      if (sellAmount && sellToken && buyToken) {
        try {
          setQuoteLoading(true);
          const q = await getUniswapQuote(sellAmount, sellToken, buyToken);
          setBuyAmount(Number.parseFloat(q).toFixed(8));
        } catch (error) {
          console.error("Error fetching updated quote:", error);
        } finally {
          setQuoteLoading(false);
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [sellAmount, sellToken, buyToken]);

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-pink-50 rounded-2xl shadow-xl p-4 w-2/4 mx-auto border border-indigo-100">
      {/* --- Header --- */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-pink-600">
          Swap Tokens
        </h2>
        {quoteLoading && (
          <div className="flex items-center text-xs text-indigo-500 font-medium">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Updating quote
          </div>
        )}
      </div>

      {/* --- You Pay Section --- */}
      <div className="bg-white rounded-xl p-5 mb-2 shadow-sm border border-gray-100">
        <label className="text-indigo-500 text-sm font-medium mb-2 block">
          You Pay
        </label>
        <div className="flex justify-between items-center mt-1">
          <input
            type="number"
            value={sellAmount}
            onChange={handleSellAmountChange}
            className="bg-transparent outline-none text-3xl font-bold w-2/3 text-gray-800 placeholder-gray-300"
            placeholder="0"
          />

          {/* Sell‐token dropdown: attach ref here */}
          <div className="relative w-28" ref={sellDropdownRef}>
            <button
              onClick={() => setCoinList((prev) => !prev)}
              className={`w-full bg-indigo-50 border border-indigo-100 rounded-full py-2 pl-3 pr-10 text-sm font-medium flex items-center justify-between hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-all ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              <div className="flex items-center gap-2">
                <img src={sellToken.icon} alt="coin" className="w-5 h-5" />
                <span>{sellToken.symbol || "Select token"}</span>
              </div>
              <div className="absolute right-[10px] top-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-indigo-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>

            {/* Sell dropdown list */}
            {coinList && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-indigo-100 rounded-md shadow-lg max-h-60 overflow-auto">
                {tokens.map((token) => (
                  <li
                    key={token.id}
                    onClick={() => {
                      handleSellTokenChange({ target: { value: token.id } });
                      setCoinList(false);
                    }}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 flex items-center gap-2 cursor-pointer"
                  >
                    <img
                      src={token.icon}
                      alt={token.symbol}
                      className="w-5 h-5"
                    />
                    {token.symbol}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* --- Swap Arrow Button --- */}
      <div className="flex justify-center -my-3 relative z-10">
        <button
          onClick={handleSwapPositions}
          disabled={loading || !sellToken || !buyToken}
          className="bg-gradient-to-r from-indigo-500 to-pink-500 rounded-full p-2 shadow-md hover:shadow-lg transform transition-all hover:scale-110 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <ArrowDownUp className="h-5 w-5 text-white" />
        </button>
      </div>

      {/* --- You Receive Section --- */}
      <div className="bg-white rounded-xl p-5 mb-6 shadow-sm border border-gray-100">
        <label className="text-indigo-500 text-sm font-medium mb-2 block">
          You Receive
        </label>
        <div className="flex justify-between items-center mt-1">
          <div className="relative w-2/3">
            <input
              type="number"
              value={buyAmount}
              onChange={handleBuyAmountChange}
              className="bg-transparent outline-none text-3xl font-bold w-full text-gray-800 placeholder-gray-300"
              placeholder="0"
              readOnly={quoteLoading}
            />
            {quoteLoading && (
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
              </div>
            )}
          </div>

          {/* Buy‐token dropdown: attach ref here */}
          <div className="relative w-28" ref={buyDropdownRef}>
            <button
              onClick={() => setCoinReceiveList((prev) => !prev)}
              className={`appearance-none bg-indigo-50 border border-indigo-100 rounded-full py-2 pl-3 pr-10 text-sm font-medium w-full flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-all hover:bg-indigo-100 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              <div className="flex items-center gap-2">
                {buyToken.symbol ? (
                  <img
                    src={buyToken.icon}
                    alt={buyToken.symbol}
                    className="w-5 h-5"
                  />
                ) : (
                  <div className="w-5 h-5 bg-gray-300 rounded-full" />
                )}
                <span>{buyToken.symbol || "Select Token"}</span>
              </div>
              <div className="absolute right-[10px] top-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-indigo-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>

            {/* Buy dropdown list */}
            {coinReceiveList && (
              <ul className="absolute z-10 mt-2 w-full bg-white border border-indigo-100 rounded-md shadow-md max-h-60 overflow-auto">
                {tokens.map((token) => (
                  <li
                    key={token.id}
                    onClick={() => {
                      handleBuyTokenChange({ target: { value: token.id } });
                      setCoinReceiveList(false);
                    }}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 flex items-center gap-2 cursor-pointer"
                  >
                    <img
                      src={token.icon}
                      alt={token.symbol}
                      className="w-5 h-5"
                    />
                    {token.symbol}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* --- Rate Display --- */}
      {sellAmount && buyAmount && (
        <div className="text-xs text-gray-500 mb-4 bg-indigo-50 p-2 rounded-lg">
          <div className="flex justify-between">
            <span>Rate</span>
            <span className="font-medium">
              1 {sellToken.symbol} ={" "}
              {(buyAmount / sellAmount).toFixed(6)} {buyToken.symbol}
            </span>
          </div>
        </div>
      )}

      {/* --- Swap Button --- */}
      {walletAddress && (
        <button
          onClick={swapTokens}
          className={`w-full py-3 rounded-xl font-medium shadow-sm transition-all ${
            loading || !sellAmount || !buyAmount
              ? "bg-gradient-to-r from-gray-400 to-gray-500 text-white opacity-70 cursor-not-allowed"
              : "bg-gradient-to-r from-indigo-600 to-pink-600 text-white hover:shadow-lg hover:from-indigo-700 hover:to-pink-700"
          }`}
          disabled={loading || !sellAmount || !buyAmount}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </div>
          ) : (
            "Swap"
          )}
        </button>
      )}
    </div>
  );
};

export default Swap;
